import { useStore } from 'effector-react';
import { useEffect } from 'react';
import Alert, { AlertSeverity } from 'components/Alert';
import FilterLine from 'components/FilterLine';
import FilterResult from 'components/FilterResult';
import LoadMoreButton from 'components/LoadMoreButton';
import NoContent from 'components/NoContent';
import {
	clearPiiFilter,
	piiFilterToArray,
	piiFilterWithGroupsStore,
} from 'components/PiiGlobalFilterV2/model';
import Preloader from 'components/Preloader';
import ResetFilters from 'components/ResetFilters';
import EnhancedTableHead, { TOrder } from 'components/table/EnhancedTableHead';
import GridBody from 'components/table/GridBody';
import GridTable from 'components/table/GridTable';
import { Typo } from 'components/typography/Typo';
import { KafkaMessageSchema, KafkaMessageSchemaListItem } from 'models/kafkaMessageSchemas/dto';
import { kafkaMessageSchemasModel } from 'models/kafkaMessageSchemas/model';
import { KafkaTopicStatus, KafkaTopicTableItem } from 'models/kafkaTopics/dto';
import { PageParamsConfig, usePageParams } from 'services/pageParams';
import tableConfig from './config';
import styles from './index.module.css';
import { SchemaRow } from './SchemaRow';

const pageConfig = {
	'data-types': {
		type: 'numberArray',
		persistence: 'session', // TODO implement 'global' in actuality
	},
	nonempty: {
		type: 'boolean',
		persistence: 'session', // TODO implement 'global' in actuality
	},
	sort: {
		type: 'sort',
		persistence: 'session',
	},
} satisfies PageParamsConfig;

type Props = {
	topicId: KafkaTopicTableItem['id'];
	hasUnsupportedContent: KafkaTopicStatus['has_unsupported_content'];
};
function MessageSchemaList({ topicId, hasUnsupportedContent }: Props) {
	const state = useStore(kafkaMessageSchemasModel.store);
	const piiFilterWithGroups = useStore(piiFilterWithGroupsStore);
	const [pageParams, setPageParams] = usePageParams(pageConfig, 'kafkaMessageSchemas');

	useEffect(() => {
		setPageParams({
			...pageParams,
			'data-types': piiFilterWithGroups.dataTypes,
			nonempty: piiFilterWithGroups.nonEmpty,
		});
	}, [piiFilterWithGroups]);

	useEffect(() => {
		const { sort } = pageParams;

		const dataTypes = piiFilterToArray(piiFilterWithGroups);
		const paramsForFx = {
			sort: { orderBy: sort.value as keyof KafkaMessageSchemaListItem, order: sort.operator },
			'data-types': dataTypes,
		};

		kafkaMessageSchemasModel.fetchFx({ id: topicId, ...paramsForFx });
	}, [pageParams]);

	// Clean up model on unmount
	useEffect(() => {
		return () => {
			kafkaMessageSchemasModel.resetFx();
		};
	}, []);
	function onSortUpdate(property: keyof KafkaMessageSchema) {
		const { sort } = pageParams;

		const operator: TOrder = sort.value === property && sort.operator === 'asc' ? 'desc' : 'asc';
		const newParams = { sort: { operator, value: property } };

		setPageParams({ ...pageParams, ...newParams });
	}

	function onResetFilters() {
		const newParams = {
			'data-types': [],
		};

		setPageParams({ ...pageParams, ...newParams });
		clearPiiFilter();
	}

	const { status, data, total, total_filtered, hasMoreData } = state;

	const hasFilter = pageParams['data-types'].length > 0;

	return (
		<>
			{data.length > 0 && hasUnsupportedContent && (
				<Alert severity={AlertSeverity.info} className={styles.alert}>
					Some messages are in unsupported or binary formats and cannot be converted to schemas
				</Alert>
			)}

			<FilterLine
				config={['dataTypes']}
				values={{
					dataTypes: pageParams['data-types'],
				}}
				onChange={() => {}}
			/>

			<FilterResult
				entityLabel="message schema"
				loading={status === 'loading'}
				total={total}
				totalFiltered={total_filtered}
			/>

			<GridTable dataTest="kafka-schemas-table">
				<EnhancedTableHead
					config={tableConfig}
					order={pageParams.sort.operator}
					orderBy={pageParams.sort.value}
					onRequestSort={onSortUpdate}
					rowClassname={styles.rowContainer}
				/>

				<Preloader isLoading={status === 'initial' || status === 'loading'}>
					<GridBody data-test="kafka-schemas-list">
						<>
							{data.length ? (
								data.map((schema) => (
									<SchemaRow key={schema.id} topicId={topicId} schema={schema} />
								))
							) : data.length === 0 && hasUnsupportedContent && !hasFilter ? (
								<div className={styles.emptyContainer}>
									<Typo variant="D/Regular/Body-S" color="secondary">
										Messages are in unsupported or binary formats and cannot be
										<br />
										converted to schemas
									</Typo>
								</div>
							) : hasFilter ? (
								<ResetFilters onReset={onResetFilters} />
							) : (
								<NoContent type="kafkaMessageSchemas" className={styles.rowContainer}>
									<div className={styles.emptyContainer}>
										<Typo variant="D/Regular/Body-S" color="secondary">
											No messages yet.
										</Typo>
									</div>
								</NoContent>
							)}

							<LoadMoreButton
								show={hasMoreData}
								loading={status === 'loadingMore'}
								request={kafkaMessageSchemasModel.fetchMoreFx}
							/>
						</>
					</GridBody>
				</Preloader>
			</GridTable>
		</>
	);
}

export default MessageSchemaList;
